import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import Spinner from "./spinner";

const buttonVariants = cva(
  "flex-shrink-0 inline-flex cursor-pointer items-center justify-center whitespace-nowrap transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          "bg-blue-500 !text-white hover:bg-blue-600 disabled:bg-blue-200",
        gradient:
          "bg-gradient-to-r from-[#7C86FF] to-[#C27AFF] !text-white hover:opacity-90 disabled:opacity-50 animate-gradient-fade-in",
        destructive:
          "bg-destructive !text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
        secondary:
          "bg-gray-100 text-gray-600 hover:bg-gray-200 !disabled:text-gray-300 animate-gradient-fade-out",
        ghost:
          "bg-transparent !text-opacity-600 hover:bg-opacity-50 !disabled:text-opacity-200",
        inverse:
          "bg-blue-100 !text-blue-500 hover:bg-blue-200 disabled:bg-blue-100 !disabled:text-blue-300",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        sm: "py-[3px] px-2 rounded-[6px] text-13m h-7",
        md: "py-[6px] px-[10px] rounded-lg text-14m h-[34px]",
        lg: "py-[9px] px-3 rounded-lg text-15m h-10",
        xl: "py-3 px-4 rounded-xl text-17m h-12",
        icon: "size-9 rounded-xl",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
    },
  }
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  loading,
  children,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    loading?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      data-slot="button"
      className={cn(
        buttonVariants({ variant, size }),
        "transition-[background,background-image,color,opacity]",
        className
      )}
      disabled={loading}
      {...props}
    >
      {loading ? <Spinner /> : children}
    </Comp>
  );
}

export { Button, buttonVariants };
